import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Sparkles, CheckCircle, ArrowRight, Clock, BarChart3, 
  Brain, FileText, TrendingUp, Users, Award, Zap
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PromoPage = () => {
  const navigate = useNavigate();
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const fetchPromo = async () => {
      try {
        const res = await axios.get(`${API}/promo/status`);
        if (res.data.is_active) {
          setPromo(res.data);
        } else {
          // Redirect to home if no active promo
          navigate('/');
        }
      } catch (err) {
        console.error('Failed to fetch promo:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchPromo();
  }, [navigate]);

  useEffect(() => {
    if (!promo?.promo_end_date) return;

    const calculateTimeLeft = () => {
      const endDate = new Date(promo.promo_end_date);
      const now = new Date();
      const difference = endDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        navigate('/');
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [promo, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!promo) return null;

  const savings = promo.original_price - promo.promo_price;
  const savingsPercent = Math.round((savings / promo.original_price) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-serif font-bold text-white">StatsXY</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
              Sign In
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-12">
        {/* Countdown Timer */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 px-4 py-2 rounded-full mb-6">
            <Clock className="w-4 h-4 text-red-400" />
            <span className="text-red-300 font-medium">Limited Time Offer</span>
          </div>
          
          <div className="flex justify-center gap-4 mb-8">
            {[
              { value: timeLeft.days, label: 'Days' },
              { value: timeLeft.hours, label: 'Hours' },
              { value: timeLeft.minutes, label: 'Minutes' },
              { value: timeLeft.seconds, label: 'Seconds' }
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 min-w-[80px]">
                <div className="text-3xl font-bold text-white">{String(item.value).padStart(2, '0')}</div>
                <div className="text-xs text-slate-400 uppercase tracking-wide">{item.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Hero Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-6 leading-tight">
              {promo.promo_title || 'Transform Your Research with AI-Powered Statistics'}
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              {promo.promo_description || 'Join thousands of students, researchers, and academics who trust StatsXY for publication-ready statistical analysis.'}
            </p>
            
            {/* Price */}
            <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 backdrop-blur-sm border border-indigo-500/30 rounded-2xl p-6 mb-8">
              <div className="flex items-end gap-4 mb-4">
                <span className="text-5xl font-bold text-white">${promo.promo_price}</span>
                <span className="text-slate-400 line-through text-2xl">${promo.original_price}</span>
                <span className="bg-green-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                  SAVE {savingsPercent}%
                </span>
              </div>
              <p className="text-slate-300">One-time payment for full access</p>
            </div>

            <Link to="/signup">
              <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-lg px-8 py-6 rounded-xl w-full sm:w-auto">
                Start Now – Save ${savings.toFixed(2)}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Media */}
          <div className="relative">
            {promo.promo_video_url ? (
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <iframe
                  src={promo.promo_video_url.replace('watch?v=', 'embed/')}
                  title="Promo Video"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : promo.promo_image_url ? (
              <img 
                src={promo.promo_image_url} 
                alt="Promo" 
                className="w-full rounded-2xl shadow-2xl border border-white/10"
              />
            ) : (
              <div className="aspect-video bg-gradient-to-br from-indigo-600/30 to-purple-600/30 rounded-2xl flex items-center justify-center border border-white/10">
                <div className="text-center">
                  <BarChart3 className="w-20 h-20 text-indigo-400 mx-auto mb-4" />
                  <p className="text-white text-xl font-medium">AI-Powered Analytics</p>
                </div>
              </div>
            )}
            
            {/* Floating badge */}
            <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl shadow-lg transform rotate-3">
              <span className="font-bold">Save ${savings.toFixed(2)}/mo!</span>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <h2 className="text-3xl font-serif font-bold text-white text-center mb-12">
            Everything You Need for Research Excellence
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: BarChart3, title: 'Quantitative Analysis', desc: 'T-tests, ANOVA, Regression, Chi-Square, and more' },
              { icon: FileText, title: 'Qualitative Analysis', desc: 'Thematic, Sentiment, Word Clouds, Critical Analysis' },
              { icon: Brain, title: 'AI Interpretations', desc: 'Get publication-ready explanations instantly' },
              { icon: TrendingUp, title: 'Visual Outputs', desc: 'Charts, graphs, and scatter plots' },
              { icon: Award, title: 'Reliability Tests', desc: "Cronbach's Alpha and validity checks" },
              { icon: Zap, title: 'Instant Results', desc: 'No coding required - results in seconds' }
            ].map((feature, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors">
                <feature.icon className="w-10 h-10 text-indigo-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Social Proof */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <Users className="w-12 h-12 text-indigo-400" />
              <div>
                <div className="text-3xl font-bold text-white">Trusted by Researchers</div>
                <div className="text-slate-400">From universities worldwide</div>
              </div>
            </div>
            <div className="flex gap-8 text-center">
              <div>
                <div className="text-2xl font-bold text-white">15+</div>
                <div className="text-slate-400 text-sm">Analysis Types</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">AI</div>
                <div className="text-slate-400 text-sm">Powered</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">24/7</div>
                <div className="text-slate-400 text-sm">Available</div>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-serif font-bold text-white mb-4">
            Don&apos;t Miss This Exclusive Offer
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Start your research journey with StatsXY today at this special promotional price. 
            This offer expires soon!
          </p>
          <Link to="/signup">
            <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-lg px-10 py-6 rounded-xl">
              Get Started Now – Only ${promo.promo_price}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="text-slate-500 mt-4 text-sm">
            Regular price: ${promo.original_price} • Secure payment via PayPal
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10 mt-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-slate-400 text-sm">
          <p>© {new Date().getFullYear()} StatsXY by Fee Doree Ltd. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PromoPage;
